import * as PIXI from 'pixi.js';
import SoundManager from 'Engine/soundManager/SoundManager';
import GlobalDispatcher from 'Engine/events/GlobalDispatcher';
import EntryPoint from 'Engine/EntryPoint';
import { eSounds } from '../enums/eSounds';
import FlashLib from 'flashlib_onlyplay';
import eSymbolIndex from '../symbols/eSymbolIndex';
import eEventTypes from '../enums/eEventTypes';
import ControllerFeatures from '../slot/features/ControllerFeatures';
import eAnimationTypes from '../enums/eAnimationTypes';
import { eAnimationNames } from '../enums/eAnimationNames';
import eGameMode from '../enums/eGameMode';

export default class ReelsContainer extends FlashLib.MovieClip {
  constructor(data, displayItemData) {
    super(data, displayItemData);

    this._freespinsTriggerIds = [eSymbolIndex.ESI_SCATTER];
    this._swapReelsHandlers = {
      [eGameMode.EGM_REGULAR]: this._setMainGameReels.bind(this),
      [eGameMode.EGM_BONUS]: this._setBonusGameReels.bind(this),
    };

    this.init();
    // this.initFeaturesController();
    this.addListeners();
    this.initReels();
  }

  init() {
    this._stopSoundPlayed = false;
    this._scatterSoundPlayed = false;
    this.hasScatter = false;
    this._scatterSymbols = [];

    this.wrapper = this.getChildByName('wrapper');
    this.ControllerReels = this.wrapper.getChildByName('controllerReels');
    this.Antisipators = this.wrapper.getChildByName('antisipator');
    this.Antisipators.minCountForStartAnimation = 2;
    this.Antisipators.maxCountForStartAnimation = 5;
    this.Antisipators.mainSoundType = eSounds.EST_REEL_ANTISIPATOR.soundName;
    this.Antisipators.mainSoundVolume = eSounds.EST_REEL_ANTISIPATOR.volume;
    this.Antisipators.mainSoundGroup = eSounds.EST_REEL_ANTISIPATOR.group;
    this.Antisipators.tileSoundType = eSounds.EST_REEL_ANTISIPATOR_TILE.soundName;
    this.Antisipators.tileSoundVolume = eSounds.EST_REEL_ANTISIPATOR_TILE.volume;
    this.Antisipators.tileSoundGroup = eSounds.EST_REEL_ANTISIPATOR_TILE.group;
    this.Antisipators.animationType = eAnimationTypes.ANTISIPATOR;
    this.Antisipators.animationName = eAnimationNames.EAN_ANTISIPATOR;
    this.Antisipators.triggerSymbolsId = [eSymbolIndex.ESI_SCATTER];
    this.Antisipators.init();

    this._drawMask();
  }

  initReels() {
    this.ControllerReels.hideBottomSymbols = false;
    this.ControllerReels.init(EntryPoint.configData.reels.availableReelsNormal);
  }

  initFeaturesController(container) {
    this.ControllerFeatures = new ControllerFeatures({ topContainer: container, reelsContainer: this, controllerReels: this.ControllerReels });
  }

  addListeners() {
    this.ControllerReels.onHittingBar = this.onHitBar.bind(this);
    this.ControllerReels.onAllReelsStopped = this.onAllReelsStopped.bind(this);
    GlobalDispatcher.add(eEventTypes.EET_FREESPIN_IN_STATE__START, this.onStartBonusGameInState, this);
  }

  show() {
    this.visible = true;
  }

  hide() {
    this.visible = false;
    this.stopAnimations();
  }

  swapReels(mode) {
    this.ControllerFeatures.reset();
    this._swapReelsHandlers[mode]();
  }

  onHitBar(reelIndex) {
    this.Antisipators.tryStopAntisipatorOnReel(reelIndex);
    this.Antisipators.tryStartAntisipatorForReel(reelIndex + 1);

    this.ControllerFeatures.onReelHitBar(reelIndex);

    const symbols = this.ControllerReels.reels[reelIndex].symbols;
    for (let i = this.ControllerReels.topSimbolsCount; i <= symbols.length - 1 - this.ControllerReels.bottomSimbolsCount; i++) {
      const symbol = symbols[i];
      if (this._freespinsTriggerIds.includes(symbol.id)) {
        this.hasScatter = true;
        this._scatterSymbols.push(symbol);
        symbol.startStopAnimation();
      }
    }

    this.playStopSound();
  }

  playStopSound() {
    if (!this._stopSoundPlayed) {
      SoundManager.play(eSounds.EST_REEL_STOP.soundName, eSounds.EST_REEL_STOP.volume + 0.2 * Math.random(), false, eSounds.EST_REEL_STOP.group);
      this._stopSoundPlayed = EntryPoint.GameSettings.quickStop;
    }

    if (this.hasScatter) {
      this.hasScatter = false;
      if (!this._scatterSoundPlayed) {
        SoundManager.play(eSounds.EST_SCATTER_STOP.soundName, eSounds.EST_SCATTER_STOP.volume + 0.2 * Math.random(), false, eSounds.EST_SCATTER_STOP.group);
        this._scatterSoundPlayed = EntryPoint.GameSettings.quickStop;
      }
    }
  }

  onAllReelsStopped() {
    GlobalDispatcher.dispatch(eEventTypes.EET_REELS_STOPPED);
  }

  onStartBonusGameInState() {
    if (!this.willShowScattersAnimation) return;
    this.ControllerFeatures.showBonusGameStart(this._scatterSymbols);
  }

  start() {
    this.ControllerFeatures.reset();
    this._stopSoundPlayed = false;
    this._scatterSoundPlayed = false;
    this._scatterSymbols = [];
    const config = this._getReelsStartConfig();
    Object.assign(this.ControllerReels, config);
    this.ControllerReels.start();
  }

  stop(data) {
    this.data = data;
    const config = this._getReelsStopConfig();
    const symbolsCountForStop = this._getSymbolsCountForStop(data, config);
    Object.assign(this.ControllerReels, config, { symbolsCountForStop });
    this.ControllerReels.stop(this.data);
  }

  showWin(data) {
    this.ControllerFeatures.showWin(data);
  }

  stopAnimations() {
    this.ControllerReels.reels.forEach(reel => reel.symbols.forEach(symbol => symbol.stopAnimation()));
  }

  _getSymbolsCountForStop(data, config) {
    let currentValues = config.hasOwnProperty('symbolsCountForStop') ? config.symbolsCountForStop : this.ControllerReels.symbolsCountForStop;
    if (!EntryPoint.GameSettings.quickStop && !EntryPoint.GameModel.freespinsEnabled) this.Antisipators.checkAntisipators(data);
    if (!this.Antisipators.involvedReels.length) return currentValues;

    let antisipatorDiff = 0;
    let antisipatorDiffStep = 40;

    const newValues = [];
    for (let i = 0; i < this.ControllerReels.reels.length; i++) {
      let value = this.ControllerReels.getConfigValue(currentValues, i);
      if (this.Antisipators.involvedReels.includes(i)) {
        antisipatorDiff += antisipatorDiffStep;
      }
      newValues.push(value + antisipatorDiff);
    }

    return newValues;
  }

  _getReelsConfig() {
    let config = EntryPoint.configData.reels.config;
    return EntryPoint.GameModel.freespinsEnabled ? config.freeSpins : config.regularSpins;
  }

  _getReelsStopConfig() {
    const config = this._getReelsConfig();
    return (EntryPoint.GameSettings.quickStop || EntryPoint.GameSettings.quickSpin) ? config.quick.stop : config.default.stop;
  }

  _getReelsStartConfig() {
    const config = this._getReelsConfig();
    return EntryPoint.GameSettings.quickSpin ? config.quick.start : config.default.start;
  }

  _setBonusGameReels() {
    this.ControllerReels.setAvailableSymbols(EntryPoint.GameModel.freespinsAvalibleReels);
    this._lastReelsMainGame = this.ControllerReels.reels.map(reel => reel.symbols.map(symbol => symbol.id))
  }

  _setMainGameReels() {
    this.ControllerReels.setAvailableSymbols(EntryPoint.configData.reels.availableReelsNormal);
    if (this._lastReelsMainGame) {
      this._lastReelsMainGame.forEach((reel, reelIndex) => {
        reel.forEach((symbolId, symbolIndex) => {
          const symbol = this.ControllerReels.reels[reelIndex].symbols[symbolIndex];
          symbol.changeSymbol(symbolId);
        })
      })
    }
  }

  _drawMask() {
    let mask = new PIXI.Graphics();
    mask.beginFill(0xFFFFFF, 1);
    mask.drawRoundedRect(0, 0, 1255, 801,4, );
    mask.endFill();
    this.addChild(mask);
    this.wrapper.mask = mask;
  }

  get willShowScattersAnimation() {
    return this._scatterSymbols.length;
  }
}
